import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import api from "../helpers/axios";
import { useDashboardUI } from "../context/GlobalContext";
import SupportTicketDrawer from "../components/SupportTicketDrawer";
import TicketStatusPill from "../components/TicketStatusPill";
import TicketPriorityPill, { TICKET_PRIORITIES } from "../components/TicketPriorityPill";
import ExportTicketsModal from "../components/ExportTicketsModal";
import FilterMultiSelect from "../components/FilterMultiSelect";

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const { selectedFacilityFilter, setPageLoader } = useDashboardUI();
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Status filter state (empty array = all statuses)
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Priority filter state (Jira urgency ids, empty array = all priorities)
  const [selectedPriorities, setSelectedPriorities] = useState([]);

  // Ticket shown in the detail drawer (null when the drawer is closed)
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Whether the export popup is open
  const [exportOpen, setExportOpen] = useState(false);

  const fetchTickets = async () => {
    try {
      setPageLoader(true);

      const params = new URLSearchParams({
        page: page,
        page_size: limit,
      });

      // Multi-valued filters: repeat the param once per selected value
      selectedStatuses.forEach((status) => params.append("status", status));
      selectedPriorities.forEach((priority) => params.append("priority", priority));

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      let apiPath = `/help-center/all-requests?${params.toString()}`;
      if (selectedFacilityFilter?.facility_number) {
        apiPath += `&facility_id=${selectedFacilityFilter.facility_number}`;
      }

      const res = await api.get(apiPath);

      const responseData = res.data;
      const ticketsData = responseData.data ?? [];
      const pagination = responseData.pagination;

      setTickets(ticketsData);
      setTotal(pagination?.total_count ?? ticketsData.length);
      setTotalPages(pagination?.total_pages ?? Math.ceil((pagination?.total_count ?? ticketsData.length) / limit) ?? 1);
      setHasNext(pagination?.has_next ?? false);
      setHasPrev(pagination?.has_prev ?? false);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
      setError("Failed to load support tickets.");
    } finally {
      setLoading(false);
      setPageLoader(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch statuses for filter dropdown
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        let apiPath = `/help-center/get-statuses`;
        if (selectedFacilityFilter?.facility_number) {
          apiPath += `?facility_id=${selectedFacilityFilter.facility_number}`;
        }
        const res = await api.get(apiPath);
        setStatuses(res.data?.data ?? []);
      } catch (err) {
        console.error("Failed to fetch statuses:", err);
      }
    };
    fetchStatuses();
  }, [selectedFacilityFilter]);

  // Reset filters and pagination when facility changes
  useEffect(() => {
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setPage(1);
  }, [selectedFacilityFilter]);

  // Fetch tickets when filters or pagination change
  useEffect(() => {
    fetchTickets();
  }, [page, limit, debouncedSearch, selectedFacilityFilter, selectedStatuses, selectedPriorities]);

  return (
    <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">
      {/* Header */}
      <div className="grid w-full max-w-full flex-shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2 sm:gap-3 lg:grid-cols-[minmax(0,1fr)_12rem_11rem_auto]">
        <input
          type="text"
          placeholder="Search by ticket number or email"
          value={search}
          onChange={(e) => {
            const newSearch = e.target.value;
            // Only reset to page 1 when actively typing, not when clearing
            if (newSearch) {
              setPage(1);
            }
            setSearch(newSearch);
          }}
          className="w-full min-w-0 h-10 sm:h-11 col-span-3 lg:col-span-1 rounded-xl border border-[#ECEEF4] bg-white px-4 sm:px-5 text-sm text-gray-700 placeholder:text-[#9AA1B2] transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
        />
        <div className="min-w-0">
          <FilterMultiSelect
            options={statuses.map((status) => ({ value: status, label: status }))}
            value={selectedStatuses}
            onChange={(values) => {
              setSelectedStatuses(values);
              setPage(1);
            }}
            placeholder="All Status"
          />
        </div>
        <div className="min-w-0">
          <FilterMultiSelect
            options={TICKET_PRIORITIES.map((priority) => ({
              value: priority.label,
              label: priority.label,
            }))}
            value={selectedPriorities}
            onChange={(values) => {
              setSelectedPriorities(values);
              setPage(1);
            }}
            placeholder="All Priority"
          />
        </div>

        <button
          onClick={() => setExportOpen(true)}
          disabled={total === 0}
          title="Export"
          aria-label="Export"
          className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#3F4BB8] w-11 sm:w-auto sm:px-6 h-11 text-sm font-semibold text-white hover:bg-[#363FA0] transition disabled:opacity-50"
        >
          <Download size={16} />
          <span className="hidden sm:inline">Export</span>
        </button>
      </div>

      {/* Table card (same look as Feedback Items / User Management): sticky header, one scroll area */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
        <div className="table-scroll h-full overflow-auto">
        <table className="w-full min-w-[1150px] xl:min-w-[960px] table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {[
                { label: "Ticket Id", width: "9%" },
                { label: "Ticket Description", width: "19%" },
                { label: "User Email", width: "24%" },
                { label: "Submitted On", width: "12%" },
                { label: "Updated On", width: "12%" },
                { label: "Priority", width: "10%" },
                { label: "Latest Status", width: "14%" },
              ].map(({ label, width }) => (
                <th
                  key={label}
                  style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, width, textAlign: "left" }}
                  className="py-3.5 pl-6 pr-3 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3674] mr-3"></div>
                    Loading support tickets...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-red-600"
                >
                  {error}
                </td>
              </tr>
            ) : tickets.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  No support tickets found
                </td>
              </tr>
            ) : (
              tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={`cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${
                    selectedTicket?.id === ticket.id
                      ? "bg-[#F5F2FF]"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="w-[9%] pl-6 pr-3 py-4 align-middle text-sm font-semibold text-[#7C3AED] whitespace-nowrap">
                    {ticket.reference_key || "-"}
                  </td>

                  <td className="w-[19%] pl-6 pr-3 py-4 align-middle text-gray-600">
                    <div className="line-clamp-2 break-words" title={ticket.description}>
                      {ticket.description || "-"}
                    </div>
                  </td>

                  <td className="w-[24%] pl-6 pr-3 py-4 align-middle text-gray-600 whitespace-nowrap">
                    {ticket.user_email || "-"}
                  </td>

                  <td className="w-[12%] pl-6 pr-3 py-4 align-middle text-gray-600">
                    {ticket.created_at
                      ? new Date(ticket.created_at).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )
                      : "-"}
                  </td>

                  <td className="w-[12%] pl-6 pr-3 py-4 align-middle text-gray-600">
                    {ticket.updated_at
                      ? new Date(ticket.updated_at).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )
                      : "-"}
                  </td>

                  <td className="w-[10%] pl-6 pr-3 py-4 align-middle">
                    <TicketPriorityPill priority={ticket.priority} />
                  </td>

                  <td className="w-[14%] pl-6 pr-3 py-4 align-middle">
                    <TicketStatusPill status={ticket.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination (same layout as Feedback Items / User Management) */}
      <div className="mt-auto pt-1 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm flex-shrink-0">
        <span>Total {total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={!hasPrev}
              onClick={() => setPage(page - 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              ‹
            </button>

            <span>
              {page} / {Math.max(1, totalPages)}
            </span>

            <button
              disabled={!hasNext}
              onClick={() => setPage(page + 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Ticket detail drawer */}
      <SupportTicketDrawer
        open={!!selectedTicket}
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />

      {/* Export popup */}
      <ExportTicketsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        filters={{
          statuses: selectedStatuses,
          priorities: selectedPriorities,
          search: debouncedSearch,
          facilityId: selectedFacilityFilter?.facility_number,
        }}
      />
    </div>
  );
};

export default SupportTickets;
